/**
 * The persona dashboards: compositions over the portfolio read models.
 *
 * Rebuilt to the approved Wave ARR target arrangements (Wave F-2B, F2B-1):
 * - hq-management (manager): portfolio health first, exception alert, contractor scorecard elevated,
 *   analytical charts grouped in 2-column grid, program lookahead, dropped individual queues.
 * - layla-qc (qcLead): gatekeeper action KPIs, approvals docket elevated to top, upcoming lookahead,
 *   findings velocity and ITP pipeline, dropped field execution work.
 * - qa-test (contractor): contractor obligation KPIs (operator queues purged), findings to answer & rectify
 *   elevated to top, upcoming witness points, own dossier only, competitor comparison dropped.
 * - north-inspector/insp-mech (inspector): today's assigned inspections (My Work) moved to TOP,
 *   inspector operational KPIs, temporal lookahead grid, punch rectifications to verify, commercial dropped.
 * - tpi-inspector (tpi): third-party witness docket moved to TOP, focused 3-metric KPI banner,
 *   assigned verifications, governing ITPs, 8 empty operator tiles & 4 empty charts purged.
 *
 * Shared 4-Tier Arrangement Grammar:
 * Persona Action Queue / Docket -> Temporal Lookahead -> Analytics & Intelligence -> Utility Footer.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

import { DataTable, type RegisterColumn } from "@/components/ui/data-table";

import { getPortfolioAnalytics, countOf, type PortfolioAnalytics } from "../api/analytics";
import { listCalendarActivities, type CalendarActivity } from "../api/calendar";
import { listFindings, type FindingSummary } from "../api/findings";
import { listItps, type ItpSummary } from "../api/itps";
import { listContractorDossiers, type ContractorDossier } from "../api/portfolio";
import { listVerifications, type VerificationSummary } from "../api/verifications";
import type { Me } from "../api/me";
import { useToken } from "../auth/sessionContext";
import { strings, term } from "../brand/strings";
import "../brand/components.css";
import { BarChart } from "../charts/BarChart";
import { CompositeChart, type CompositeMonth } from "../charts/CompositeChart";
import { CauseTreemap, type CauseDatum } from "../charts/CauseTreemap";
import { DivergingBars, type DivergingRow } from "../charts/DivergingBars";
import { LineChart } from "../charts/LineChart";
import { Sparkline } from "../charts/Sparkline";
import { StackedBarChart } from "../charts/StackedBarChart";
import {
  EmptyState,
  LoadingLine,
  SkeletonChart,
  SkeletonTiles,
  RegisterTh,
  Section,
  StateChip,
  day,
  nextSort,
  sortRows,
} from "./parts";
import type { HeaderFilter, HeaderSort } from "./parts";
import { useQuery } from "./useQuery";
import {
  DONE_VERIFICATION_STATES,
  OPEN_FINDING_STATES,
  QUEUES,
  QUEUE_PARAM,
  findingsRoute,
} from "../nav/queues";
import { MonthGrid } from "../calendar/MonthGrid";
import { monthCells, toISO } from "../calendar/dateMath";
import { statusDotTone, type CalendarCell } from "../calendar/model";

/** The persona shapes targeted by the ARR arrangement. */
export type DashboardPersona = "manager" | "qcLead" | "contractor" | "inspector" | "tpi";

/**
 * Resolve the persona scope for the dashboard canvas from the WORKFLOW CLASS the server
 * derives from role assignments. Product code never names a demo account (coherence
 * review R08, wave W0). Since wave W1 the server answers `tpi` for a third party
 * inspection agency's people (ruling 6), so the TPI reads its own canvas; the read-only
 * `management` class reads the executive canvas.
 */
export function resolveDashboardPersona(me: Me): DashboardPersona {
  switch (me.workflowClass) {
    case "tpi":
      return "tpi";
    case "inspector":
      return "inspector";
    case "qc_lead":
      return "qcLead";
    case "contractor":
      return "contractor";
    default:
      return "manager";
  }
}

/** The finding states the vocabulary counts as open (one home: nav/queues.ts). */
export { OPEN_FINDING_STATES };

/**
 * Wave W1 (R13): a tile lands on EXACTLY the set it counts. A tile that sums several
 * finding states opens the findings register filtered to all of them, never to one.
 */
const OPEN_FINDINGS_ROUTE = findingsRoute(OPEN_FINDING_STATES);
const RESPONSES_DUE_ROUTE = findingsRoute(["RESPONSE_PROPOSED", "AWAITING_RESPONSE"]);

/** The verification states that are over, so an unmet date on one is overdue. */
const CLOSED_VERIFICATION_STATES = new Set(["CLOSED", "CANCELLED"]);

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoOffset(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function monthClassRows(findings: readonly FindingSummary[]): DivergingRow[] {
  const byMonth = new Map<string, { positive: number; negative: number }>();
  for (const finding of findings) {
    if (
      finding.findingClass !== "POSITIVE_OBSERVATION" &&
      finding.findingClass !== "NEGATIVE_OBSERVATION"
    ) {
      continue;
    }
    const month = finding.observedAt.slice(0, 7);
    const row = byMonth.get(month) ?? { positive: 0, negative: 0 };
    if (finding.findingClass === "POSITIVE_OBSERVATION") {
      row.positive += 1;
    } else {
      row.negative += 1;
    }
    byMonth.set(month, row);
  }
  return [...byMonth.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, row]) => ({ label: month, positive: row.positive, negative: row.negative }));
}

function causeRows(analytics: PortfolioAnalytics): CauseDatum[] {
  const table: Record<string, string> = { ...strings.terms.rootCauseCategories };
  return analytics.rootCauseDistribution
    .filter((row) => row.value !== null && row.count > 0)
    .map((row) => ({
      name: table[row.value ?? ""] ?? strings.terms.fallback(row.value ?? ""),
      value: row.count,
    }));
}

type TileTone = "success" | "attention" | "danger" | "neutral";

interface TileProps {
  readonly label: string;
  readonly count: number;
  readonly empty: string;
  readonly to: string;
  readonly action: string;
  readonly testid: string;
  readonly tone: TileTone;
  readonly sparkSnapshot?: boolean;
}

function Tile({
  label,
  count,
  empty,
  to,
  action,
  testid,
  tone,
  sparkSnapshot,
}: TileProps): React.JSX.Element {
  const toneClass = tone === "neutral" ? "" : ` q-tile--${tone}`;
  return (
    <div className={`q-tile${toneClass}`} data-testid={testid} data-tone={tone}>
      <p className="q-tile__label">{label}</p>
      {count === 0 ? (
        <p className="q-tile__body">{empty}</p>
      ) : (
        <p className="q-tile__count">{String(count)}</p>
      )}
      {sparkSnapshot === true && count > 0 && (
        <Sparkline ariaLabel={strings.dashboard.sparkAria(label)} values={[count, count]} />
      )}
      <Link className="q-tile__link" to={to}>
        {action}
      </Link>
    </div>
  );
}

function percentOrNull(value: number | null): string {
  return value === null ? "" : strings.dashboard.percent(value);
}

const PAIR_LIMIT = 5;

interface PairColumn {
  readonly key: string;
  readonly label: string;
  readonly filterOptions?: readonly { readonly value: string; readonly label: string }[];
}

interface PairRow {
  readonly key: string;
  readonly values: Record<string, string | number | null>;
  readonly cells: readonly React.ReactNode[];
}

function PairList({
  caption,
  viewAllTo,
  columns,
  rows,
}: {
  caption: string;
  viewAllTo: string;
  columns: readonly PairColumn[];
  rows: readonly PairRow[];
}): React.JSX.Element {
  const [sort, setSort] = useState<HeaderSort>({ key: null, dir: "asc" });
  const [filters, setFilters] = useState<Record<string, readonly string[]>>({});
  const filtered = rows.filter((row) =>
    columns.every((column) => {
      const active = filters[column.key] ?? [];
      return active.length === 0 || active.includes(String(row.values[column.key] ?? ""));
    }),
  );
  const sorted = sortRows(
    filtered,
    sort,
    Object.fromEntries(columns.map((c) => [c.key, (row: PairRow) => row.values[c.key] ?? null])),
  );
  return (
    <>
      <div className="q-pair-scroll" data-testid="pair-scroll">
        <table className="q-register" data-testid="pair-list">
          <caption>{caption}</caption>
          <thead>
            <tr>
              {columns.map((column) => {
                const filterProp =
                  column.filterOptions === undefined
                    ? {}
                    : {
                        filter: {
                          values: filters[column.key] ?? [],
                          options: column.filterOptions,
                          onChange: (next: readonly string[]): void => {
                            setFilters((current) => ({ ...current, [column.key]: next }));
                          },
                        } satisfies HeaderFilter,
                      };
                return (
                  <RegisterTh
                    key={column.key}
                    label={column.label}
                    sortKey={column.key}
                    sort={sort}
                    onSort={(key) => {
                      setSort(nextSort(sort, key));
                    }}
                    {...filterProp}
                  />
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={row.key}>
                {row.cells.map((cell, index) => (
                  <td key={columns[index]?.key ?? String(index)}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="q-row">
        <Link className="q-link" to={viewAllTo} data-testid="view-all">
          {strings.dashboard.viewAll}
        </Link>
      </p>
    </>
  );
}

function TrendPair({ rows }: { rows: readonly FindingSummary[] }): React.JSX.Element | null {
  if (rows.length === 0) {
    return null;
  }
  const statusOptions = [...new Set(rows.map((finding) => finding.findingStatus))].map((state) => ({
    value: state,
    label: term(state),
  }));
  return (
    <PairList
      caption={strings.dashboard.trendPairHeading}
      viewAllTo="/findings"
      columns={[
        { key: "humanId", label: strings.common.humanId },
        { key: "status", label: strings.common.status, filterOptions: statusOptions },
      ]}
      rows={rows.map((finding) => ({
        key: finding.id,
        values: { humanId: finding.humanId, status: finding.findingStatus },
        cells: [
          <>
            <Link to={`/findings/${finding.id}`} className="q-register__mono">
              {finding.humanId}
            </Link>
            <div className="q-register__wrap">{finding.description}</div>
          </>,
          <StateChip state={finding.findingStatus} />,
        ],
      }))}
    />
  );
}

function OpenItemsPair({ analytics }: { analytics: PortfolioAnalytics }): React.JSX.Element {
  return (
    <PairList
      caption={strings.dashboard.openItemsHeading}
      viewAllTo="/evaluations"
      columns={[
        { key: "party", label: strings.dashboard.columnParty },
        { key: "findings", label: strings.dashboard.openItemsFindings },
        { key: "actions", label: strings.dashboard.openItemsActions },
      ]}
      rows={analytics.contractorOpenItems.slice(0, PAIR_LIMIT).map((row) => ({
        key: row.contractorPartyId,
        values: {
          party: row.contractorPartyName,
          findings: row.openFindings,
          actions: row.openActions,
        },
        cells: [
          <Link to={`/evaluations?party=${row.contractorPartyId}`} className="q-register__mono">
            {row.contractorPartyName}
          </Link>,
          String(row.openFindings),
          String(row.openActions),
        ],
      }))}
    />
  );
}

function ItpPair({ rows }: { rows: readonly ItpSummary[] }): React.JSX.Element | null {
  if (rows.length === 0) {
    return null;
  }
  const stateOptions = [...new Set(rows.map((itp) => itp.workflowState))].map((state) => ({
    value: state,
    label: term(state),
  }));
  return (
    <PairList
      caption={strings.dashboard.myItpsHeading}
      viewAllTo="/itps"
      columns={[
        { key: "plan", label: strings.itps.columnPlan },
        { key: "state", label: strings.itps.columnState, filterOptions: stateOptions },
      ]}
      rows={rows.slice(0, PAIR_LIMIT).map((itp) => ({
        key: itp.id,
        values: { plan: itp.humanId, state: itp.workflowState },
        cells: [
          <>
            <Link to={`/itps/${itp.id}`} className="q-register__mono">
              {itp.humanId}
            </Link>
            <div className="q-register__wrap">{itp.title}</div>
          </>,
          <StateChip state={itp.workflowState} />,
        ],
      }))}
    />
  );
}

function TrendChart({ analytics }: { analytics: PortfolioAnalytics }): React.JSX.Element {
  const labels = analytics.monthly.findingsRaised.map((bucket) => bucket.month);
  return (
    <LineChart
      ariaLabel={strings.dashboard.trendHeading}
      labels={labels}
      series={[
        {
          label: strings.dashboard.raisedSeries,
          points: analytics.monthly.findingsRaised.map((bucket) => bucket.count),
        },
        {
          label: strings.dashboard.closedSeries,
          points: analytics.monthly.findingsClosed.map((bucket) => bucket.count),
        },
        {
          label: strings.dashboard.conductedSeries,
          points: analytics.monthly.verificationsConducted.map((bucket) => bucket.count),
        },
      ]}
    />
  );
}

function QualityChart({ analytics }: { analytics: PortfolioAnalytics }): React.JSX.Element {
  const navigate = useNavigate();
  const months: CompositeMonth[] = analytics.monthly.verificationsConducted.map((bucket) => ({
    month: bucket.month,
    bar: bucket.count,
    line: null,
  }));
  return (
    <CompositeChart
      ariaLabel={strings.dashboard.qualityHeading}
      barLabel={strings.dashboard.conductedSeries}
      lineLabel={strings.dashboard.columnPassRate}
      months={months}
      onSelectMonth={() => {
        void navigate("/inspections");
      }}
    />
  );
}

function ObservationsChart({
  findings,
}: {
  findings: readonly FindingSummary[];
}): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <DivergingBars
      ariaLabel={strings.dashboard.divergingHeading}
      data={monthClassRows(findings)}
      onSelectRow={(side) => {
        void navigate(
          side === "positive"
            ? "/findings?class=POSITIVE_OBSERVATION"
            : "/findings?class=NEGATIVE_OBSERVATION",
        );
      }}
    />
  );
}

function RootCauseChart({ analytics }: { analytics: PortfolioAnalytics }): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <CauseTreemap
      ariaLabel={strings.dashboard.rootCauseHeading}
      data={causeRows(analytics)}
      onSelectDatum={() => {
        void navigate("/findings");
      }}
    />
  );
}

function OpenItemsChart({ analytics }: { analytics: PortfolioAnalytics }): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <StackedBarChart
      ariaLabel={strings.dashboard.openItemsHeading}
      series={[
        { key: "findings", label: strings.dashboard.openItemsFindings },
        { key: "actions", label: strings.dashboard.openItemsActions },
      ]}
      data={analytics.contractorOpenItems.map((row) => ({
        label: row.contractorPartyName,
        segments: [
          { key: "findings", value: row.openFindings },
          { key: "actions", value: row.openActions },
        ],
      }))}
      onSelectSegment={(seriesKey) => {
        void navigate(seriesKey === "findings" ? "/findings?state=OPEN" : "/actions");
      }}
    />
  );
}

function ItpStates({ analytics }: { analytics: PortfolioAnalytics }): React.JSX.Element {
  const navigate = useNavigate();
  const rows = analytics.itpStates.filter((row) => row.value !== null && row.count > 0);
  if (rows.length === 0) {
    return (
      <EmptyState title={strings.dashboard.myItpsEmpty} body={strings.dashboard.myItpsEmpty} />
    );
  }
  return (
    <BarChart
      ariaLabel={strings.dashboard.myItpsHeading}
      data={rows.map((row) => ({ label: term(row.value), value: row.count }))}
      onSelectDatum={(_datum, index) => {
        const token = rows[index]?.value;
        if (token !== null && token !== undefined) {
          void navigate(`/itps?state=${token}`);
        }
      }}
    />
  );
}

function dossierColumns(): RegisterColumn<ContractorDossier>[] {
  const copy = strings.dashboard;
  return [
    {
      id: "party",
      label: copy.columnParty,
      text: (row) => row.partyName,
      sortValue: (row) => row.partyName,
      render: (row) => (
        <Link
          to={`/evaluations?party=${row.partyId}`}
          className="tracking-[0.03em] whitespace-nowrap text-gold no-underline hover:underline font-mono"
        >
          {row.partyName}
        </Link>
      ),
    },
    {
      id: "inspections",
      label: copy.columnInspections,
      text: (row) => String(row.inspectionsConducted),
      sortValue: (row) => row.inspectionsConducted,
    },
    {
      id: "passRate",
      label: copy.columnPassRate,
      text: (row) => percentOrNull(row.firstTimePassRate),
      sortValue: (row) => row.firstTimePassRate ?? -1,
    },
    {
      id: "timeliness",
      label: copy.columnTimeliness,
      text: (row) => percentOrNull(row.responseTimeliness.rate),
      sortValue: (row) => row.responseTimeliness.rate ?? -1,
    },
    {
      id: "meanOpen",
      label: copy.columnMeanOpen,
      text: (row) => (row.meanDaysOpenClosed === null ? "" : copy.days(row.meanDaysOpenClosed)),
      sortValue: (row) => row.meanDaysOpenClosed ?? -1,
    },
    {
      id: "overdue",
      label: copy.columnOverdue,
      text: (row) => String(row.overdueActions),
      sortValue: (row) => row.overdueActions,
    },
  ];
}

function DossierTable({ rows }: { rows: readonly ContractorDossier[] }): React.JSX.Element {
  const columns = useMemo(() => dossierColumns(), []);
  return (
    <div className="q-ui">
      <DataTable
        data={rows}
        columns={columns}
        caption={strings.dashboard.contractorStatsHeading}
        testId="dossier"
        getRowId={(row) => row.partyId}
        getRowTestId={() => "dossier-row"}
      />
    </div>
  );
}

function FeedTable({
  rows,
  testid,
}: {
  rows: readonly CalendarActivity[];
  testid: string;
}): React.JSX.Element {
  return (
    <div className="q-scroll">
      <table className="q-register" data-testid={testid}>
        <thead>
          <tr>
            <th scope="col">{strings.common.humanId}</th>
            <th scope="col">{strings.common.date}</th>
            <th scope="col">{strings.dashboard.columnParty}</th>
            <th scope="col">{strings.common.status}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((activity) => (
            <tr key={activity.id}>
              <td>
                <Link to={`/activities/${activity.id}`} className="q-register__mono">
                  {activity.humanId}
                </Link>
              </td>
              <td>{day(activity.plannedDate)}</td>
              <td>{activity.contractorPartyName}</td>
              <td>
                <StateChip state={activity.status} label={activity.statusLabel} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MyWork({ rows }: { rows: readonly VerificationSummary[] }): React.JSX.Element {
  const today = isoToday();
  return (
    <div className="q-scroll">
      <table className="q-register" data-testid="my-work-table">
        <thead>
          <tr>
            <th scope="col">{strings.inspections.columnRecord}</th>
            <th scope="col">{strings.inspections.columnDate}</th>
            <th scope="col">{strings.inspections.columnContractor}</th>
            <th scope="col">{strings.inspections.columnState}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((record) => {
            const overdue =
              !CLOSED_VERIFICATION_STATES.has(record.workflowState) && record.plannedDate <= today;
            return (
              <tr key={record.id}>
                <td>
                  <Link to={`/inspections/${record.id}`} className="q-register__mono">
                    {record.humanId}
                  </Link>
                  <div className="q-register__wrap">{record.title}</div>
                </td>
                <td>
                  {day(record.plannedDate)}
                  {overdue && (
                    <span className="q-chip q-chip--danger" data-testid="overdue-badge">
                      {strings.dashboard.overdueBadge}
                    </span>
                  )}
                </td>
                <td>{record.contractorPartyName}</td>
                <td>
                  <StateChip state={record.workflowState} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MyCalendar({ rows }: { rows: readonly CalendarActivity[] }): React.JSX.Element {
  const navigate = useNavigate();
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const byDate = new Map<string, CalendarActivity[]>();
  for (const activity of rows) {
    const list = byDate.get(activity.plannedDate) ?? [];
    list.push(activity);
    byDate.set(activity.plannedDate, list);
  }
  const cells: (CalendarCell & { year: number; month: number })[] = monthCells(year, month).map(
    (cell) => ({
      ...cell,
      dots: (byDate.get(cell.iso) ?? []).map((one) => ({
        tone: statusDotTone(one.status),
        label: `${one.humanId} - ${one.statusLabel}`,
      })),
      shade: null,
    }),
  );
  // Wave W1.5 dead-control sweep: a day with records is a button, and it used to do
  // nothing here. It opens that day on the calendar.
  return (
    <MonthGrid
      cells={cells}
      onSelectDay={(iso) => {
        void navigate(`/calendar?day=${iso}`);
      }}
    />
  );
}

/* ------------------------------------------------------------------ */
/* ARR Target Additions: Executive Risk Alert, Approvals Docket,     */
/* Findings to Answer, and Verification Punch List                    */
/* ------------------------------------------------------------------ */

/** The first-time pass rate under which a contractor is flagged (ARR §5.1). */
export const RISK_PASS_RATE_THRESHOLD = 85;

/**
 * Why one contractor is on the alert, in plain words (wave W1.5, Mahmoud's walk finding
 * 6: "100% pass, 1 overdue" with no link and no reason read as a contradiction). Each
 * rule that fired is named; a contractor listed only for an overdue action says so.
 */
export function riskReasons(dossier: ContractorDossier): string[] {
  const words = strings.dashboard;
  const rate = dossier.firstTimePassRate;
  const lowRate = rate !== null && rate < RISK_PASS_RATE_THRESHOLD;
  const reasons: string[] = [];
  if (lowRate) {
    reasons.push(words.riskReasonPassRate(rate, RISK_PASS_RATE_THRESHOLD));
  }
  if (dossier.overdueActions > 0) {
    reasons.push(words.riskReasonOverdue(dossier.overdueActions));
    if (!lowRate && rate !== null) {
      reasons.push(words.riskReasonOnlyOverdue(rate, dossier.overdueActions));
    }
  }
  return reasons;
}

export function isAtRisk(dossier: ContractorDossier): boolean {
  return riskReasons(dossier).length > 0;
}

/**
 * Executive Exception & Risk Alert Card (ARR §5.1, ADD Position 2). Every contractor it
 * names links to its dossier and carries the rule that put it there.
 */
function ExecutiveAlert({
  dossiers,
}: {
  dossiers: readonly ContractorDossier[];
}): React.JSX.Element {
  const words = strings.dashboard;
  const atRisk = dossiers.filter(isAtRisk);
  if (atRisk.length === 0) {
    return (
      <div className="q-alert-card q-alert-card--success" data-testid="executive-alert">
        <p className="q-note__title">{words.riskHealthyHeading}</p>
        <p className="q-note__body">{words.riskHealthyBody(RISK_PASS_RATE_THRESHOLD)}</p>
      </div>
    );
  }
  return (
    <div className="q-alert-card q-alert-card--attention" data-testid="executive-alert">
      <p className="q-note__title">{words.riskAlertHeading}</p>
      <p className="q-note__body" data-testid="executive-alert-rule">
        {words.riskAlertRule(RISK_PASS_RATE_THRESHOLD)}
      </p>
      <p className="q-note__body">{words.riskAlertCount(atRisk.length)}</p>
      <ul className="q-note__body" data-testid="executive-alert-list">
        {atRisk.map((dossier) => (
          <li key={dossier.partyId} data-testid="executive-alert-item">
            <Link
              to={`/evaluations?party=${dossier.partyId}`}
              className="q-link"
              aria-label={words.riskOpenDossier(dossier.partyName)}
              data-testid="executive-alert-link"
            >
              {dossier.partyName}
            </Link>
            {": "}
            {riskReasons(dossier).join("; ")}.
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Primary Approval Work Queue (Approvals Docket, ARR §5.2, ADD Position 2).
 * Actionable multi-stream sign-off docket for the QC lead.
 */
function ApprovalsDocket({
  submittedItps,
  finalVerifications,
  implementedFindings,
}: {
  submittedItps: readonly ItpSummary[];
  finalVerifications: readonly VerificationSummary[];
  implementedFindings: readonly FindingSummary[];
}): React.JSX.Element {
  // B-2 contract request: a dedicated GET /api/approvals/pending endpoint would consolidate
  // pending approvals across ITPs, verifications, and schedules into a single authoritative queue.
  const total = submittedItps.length + finalVerifications.length + implementedFindings.length;
  if (total === 0) {
    return (
      <div data-testid="approvals-docket">
        <EmptyState
          title="No pending approvals"
          body="No plans, inspection results, or rectifications are currently awaiting your signature."
        />
      </div>
    );
  }
  return (
    <div className="q-scroll" data-testid="approvals-docket">
      <table className="q-register">
        <caption>Pending Approvals Docket</caption>
        <thead>
          <tr>
            <th scope="col">Record</th>
            <th scope="col">Type</th>
            <th scope="col">Party</th>
            <th scope="col">State</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {submittedItps.map((itp) => (
            <tr key={`itp-${itp.id}`}>
              <td>
                <Link to={`/itps/${itp.id}`} className="q-register__mono">
                  {itp.humanId}
                </Link>
                <div className="q-register__wrap">{itp.title}</div>
              </td>
              <td>Plan (ITP)</td>
              <td>{itp.contractorPartyName}</td>
              <td>
                <StateChip state={itp.workflowState} />
              </td>
              <td>
                <Link to={`/itps/${itp.id}`} className="q-link">
                  Review plan
                </Link>
              </td>
            </tr>
          ))}
          {finalVerifications.map((record) => (
            <tr key={`ver-${record.id}`}>
              <td>
                <Link to={`/inspections/${record.id}`} className="q-register__mono">
                  {record.humanId}
                </Link>
                <div className="q-register__wrap">{record.title}</div>
              </td>
              <td>Inspection Result</td>
              <td>{record.contractorPartyName}</td>
              <td>
                <StateChip state={record.workflowState} />
              </td>
              <td>
                <Link to={`/inspections/${record.id}`} className="q-link">
                  Approve result
                </Link>
              </td>
            </tr>
          ))}
          {implementedFindings.map((finding) => (
            <tr key={`fnd-${finding.id}`}>
              <td>
                <Link to={`/findings/${finding.id}`} className="q-register__mono">
                  {finding.humanId}
                </Link>
                <div className="q-register__wrap">{finding.description}</div>
              </td>
              <td>Rectification</td>
              <td>{term(finding.findingClass)}</td>
              <td>
                <StateChip state={finding.findingStatus} />
              </td>
              <td>
                <Link to={`/findings/${finding.id}`} className="q-link">
                  Verify closure
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Findings to Answer & Rectify (ARR §5.3, ADD Position 2 / UX_SPEC §6).
 * Actionable register of open findings assigned to the contractor.
 */
function FindingsToAnswerTable({ rows }: { rows: readonly FindingSummary[] }): React.JSX.Element {
  if (rows.length === 0) {
    return (
      <div data-testid="findings-to-answer">
        <EmptyState
          title="No findings to answer"
          body="No open findings require response proposal or rectification implementation."
        />
      </div>
    );
  }
  return (
    <div className="q-scroll" data-testid="findings-to-answer">
      <table className="q-register">
        <caption>Findings to Answer & Rectify</caption>
        <thead>
          <tr>
            <th scope="col">{strings.findings.columnFinding}</th>
            <th scope="col">{strings.findings.columnClass}</th>
            <th scope="col">{strings.findings.observedOnLabel}</th>
            <th scope="col">{strings.findings.columnDaysOpen}</th>
            <th scope="col">{strings.findings.columnState}</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((finding) => (
            <tr key={finding.id}>
              <td>
                <Link to={`/findings/${finding.id}`} className="q-register__mono">
                  {finding.humanId}
                </Link>
                <div className="q-register__wrap">{finding.description}</div>
              </td>
              <td>{term(finding.findingClass)}</td>
              <td>{day(finding.observedAt)}</td>
              <td>{finding.daysOpen !== null ? String(finding.daysOpen) : ""}</td>
              <td>
                <StateChip state={finding.findingStatus} />
              </td>
              <td>
                <Link to={`/findings/${finding.id}`} className="q-link">
                  Respond
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Implemented Findings Awaiting Field Verification (ARR §5.4, ADD Position 4).
 * Actionable punch-list table of contractor rectifications ready for inspector witness and sign-off.
 */
function FindingsAwaitingVerificationTable({
  rows,
}: {
  rows: readonly FindingSummary[];
}): React.JSX.Element {
  if (rows.length === 0) {
    return (
      <div data-testid="findings-awaiting-verification">
        <EmptyState
          title="No rectifications to verify"
          body="No contractor rectifications are currently awaiting field verification."
        />
      </div>
    );
  }
  return (
    <div className="q-scroll" data-testid="findings-awaiting-verification">
      <table className="q-register">
        <caption>Implemented Findings Awaiting Field Verification</caption>
        <thead>
          <tr>
            <th scope="col">{strings.findings.columnFinding}</th>
            <th scope="col">{strings.findings.columnClass}</th>
            <th scope="col">{strings.findings.observedOnLabel}</th>
            <th scope="col">{strings.findings.columnDaysOpen}</th>
            <th scope="col">{strings.findings.columnState}</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((finding) => (
            <tr key={finding.id}>
              <td>
                <Link to={`/findings/${finding.id}`} className="q-register__mono">
                  {finding.humanId}
                </Link>
                <div className="q-register__wrap">{finding.description}</div>
              </td>
              <td>{term(finding.findingClass)}</td>
              <td>{day(finding.observedAt)}</td>
              <td>{finding.daysOpen !== null ? String(finding.daysOpen) : ""}</td>
              <td>
                <StateChip state={finding.findingStatus} />
              </td>
              <td>
                <Link to={`/findings/${finding.id}`} className="q-link">
                  Witness & close
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Per-Persona KPI Stat Tile Banners (ARR §5)                         */
/* ------------------------------------------------------------------ */

function ExecutiveStatTiles({ analytics }: { analytics: PortfolioAnalytics }): React.JSX.Element {
  const totalInspections = analytics.monthly.verificationsConducted.reduce(
    (sum, bucket) => sum + bucket.count,
    0,
  );
  return (
    <div className="q-tiles" data-testid="stat-tiles">
      <Tile
        testid="tile-inspections"
        label={strings.dashboard.tileTotalInspections.label}
        count={totalInspections}
        empty={strings.dashboard.tileTotalInspections.empty}
        to="/inspections"
        action={strings.dashboard.tileTotalInspections.action}
        tone="success"
        sparkSnapshot
      />
      <Tile
        testid="tile-negative"
        label={strings.dashboard.tileNegative.label}
        count={countOf(analytics.findingClasses, "NEGATIVE_OBSERVATION")}
        empty={strings.dashboard.tileNegative.empty}
        to="/findings?class=NEGATIVE_OBSERVATION"
        action={strings.dashboard.tileNegative.action}
        tone="danger"
        sparkSnapshot
      />
      <Tile
        testid="tile-positive"
        label={strings.dashboard.tilePositive.label}
        count={countOf(analytics.findingClasses, "POSITIVE_OBSERVATION")}
        empty={strings.dashboard.tilePositive.empty}
        to="/findings?class=POSITIVE_OBSERVATION"
        action={strings.dashboard.tilePositive.action}
        tone="success"
        sparkSnapshot
      />
      <Tile
        testid="tile-open-findings"
        label={strings.dashboard.tileOpenFindings.label}
        count={OPEN_FINDING_STATES.reduce(
          (sum, token) => sum + countOf(analytics.findingStates, token),
          0,
        )}
        empty={strings.dashboard.tileOpenFindings.empty}
        to={OPEN_FINDINGS_ROUTE}
        action={strings.dashboard.tileOpenFindings.action}
        tone="attention"
        sparkSnapshot
      />
      <Tile
        testid="tile-itps-submitted"
        label={strings.dashboard.tileItpsSubmitted.label}
        count={countOf(analytics.itpStates, "SUBMITTED")}
        empty={strings.dashboard.tileItpsSubmitted.empty}
        to="/itps?state=SUBMITTED"
        action={strings.dashboard.tileItpsSubmitted.action}
        tone="attention"
        sparkSnapshot
      />
    </div>
  );
}

function QcLeadStatTiles({ analytics }: { analytics: PortfolioAnalytics }): React.JSX.Element {
  return (
    <div className="q-tiles" data-testid="stat-tiles">
      <Tile
        testid="tile-itps-submitted"
        label={strings.dashboard.tileItpsSubmitted.label}
        count={countOf(analytics.itpStates, "SUBMITTED")}
        empty={strings.dashboard.tileItpsSubmitted.empty}
        to="/itps?state=SUBMITTED"
        action={strings.dashboard.tileItpsSubmitted.action}
        tone="attention"
        sparkSnapshot
      />
      <Tile
        testid="tile-schedules-queue"
        label={strings.dashboard.tileSchedulesToApprove.label}
        count={
          countOf(analytics.scheduleStates, "SUBMITTED") +
          countOf(analytics.scheduleStates, "ENDORSED")
        }
        empty={strings.dashboard.tileSchedulesToApprove.empty}
        to="/schedules?state=SUBMITTED,ENDORSED"
        action={strings.dashboard.tileSchedulesToApprove.action}
        tone="attention"
        sparkSnapshot
      />
      <Tile
        testid="tile-results-final"
        label={strings.dashboard.tileResultsFinal.label}
        count={countOf(analytics.verificationStates, "FINAL_APPROVAL")}
        empty={strings.dashboard.tileResultsFinal.empty}
        to="/inspections?state=FINAL_APPROVAL"
        action={strings.dashboard.tileResultsFinal.action}
        tone="attention"
        sparkSnapshot
      />
      <Tile
        testid="tile-awaiting-verification"
        label={strings.dashboard.tileAwaitingVerification.label}
        count={countOf(analytics.findingStates, "IMPLEMENTED")}
        empty={strings.dashboard.tileAwaitingVerification.empty}
        to="/findings?state=IMPLEMENTED"
        action={strings.dashboard.tileAwaitingVerification.action}
        tone="attention"
        sparkSnapshot
      />
    </div>
  );
}

function ContractorStatTiles({ analytics }: { analytics: PortfolioAnalytics }): React.JSX.Element {
  const totalInspections = analytics.monthly.verificationsConducted.reduce(
    (sum, bucket) => sum + bucket.count,
    0,
  );
  return (
    <div className="q-tiles" data-testid="stat-tiles">
      <Tile
        testid="tile-inspections"
        label={strings.dashboard.tileTotalInspections.label}
        count={totalInspections}
        empty={strings.dashboard.tileTotalInspections.empty}
        to="/inspections"
        action={strings.dashboard.tileTotalInspections.action}
        tone="success"
        sparkSnapshot
      />
      <Tile
        testid="tile-negative"
        label={strings.dashboard.tileNegative.label}
        count={countOf(analytics.findingClasses, "NEGATIVE_OBSERVATION")}
        empty={strings.dashboard.tileNegative.empty}
        to="/findings?class=NEGATIVE_OBSERVATION"
        action={strings.dashboard.tileNegative.action}
        tone="danger"
        sparkSnapshot
      />
      <Tile
        testid="tile-open-findings"
        label={strings.dashboard.tileOpenFindings.label}
        count={OPEN_FINDING_STATES.reduce(
          (sum, token) => sum + countOf(analytics.findingStates, token),
          0,
        )}
        empty={strings.dashboard.tileOpenFindings.empty}
        to={OPEN_FINDINGS_ROUTE}
        action={strings.dashboard.tileOpenFindings.action}
        tone="attention"
        sparkSnapshot
      />
      <Tile
        testid="tile-itps-submitted"
        label={strings.dashboard.tileItpsSubmitted.label}
        count={countOf(analytics.itpStates, "SUBMITTED")}
        empty={strings.dashboard.tileItpsSubmitted.empty}
        to="/itps?state=SUBMITTED"
        action={strings.dashboard.tileItpsSubmitted.action}
        tone="attention"
        sparkSnapshot
      />
      <Tile
        testid="tile-responses-due"
        label={strings.dashboard.tileResponsesDue.label}
        count={
          countOf(analytics.findingStates, "RESPONSE_PROPOSED") +
          countOf(analytics.findingStates, "AWAITING_RESPONSE")
        }
        empty={strings.dashboard.tileResponsesDue.empty}
        to={RESPONSES_DUE_ROUTE}
        action={strings.dashboard.tileResponsesDue.action}
        tone="attention"
        sparkSnapshot
      />
    </div>
  );
}

function InspectorStatTiles({
  analytics,
  assignedCount,
  pendingCount,
}: {
  analytics: PortfolioAnalytics;
  assignedCount: number;
  pendingCount: number;
}): React.JSX.Element {
  return (
    <div className="q-tiles" data-testid="stat-tiles">
      <Tile
        testid="tile-inspections"
        label="Assigned inspections"
        count={assignedCount}
        empty="No assigned inspections."
        to={`/inspections?queue=${QUEUE_PARAM.assignedInspections}`}
        action="Open my inspections"
        tone="success"
        sparkSnapshot
      />
      <Tile
        testid="tile-inspections-pending"
        label="Awaiting result entry"
        count={pendingCount}
        empty="No inspections awaiting result."
        to={QUEUES.myInspections.route}
        action="Open pending inspections"
        tone="attention"
        sparkSnapshot
      />
      <Tile
        testid="tile-awaiting-verification"
        label={strings.dashboard.tileAwaitingVerification.label}
        count={countOf(analytics.findingStates, "IMPLEMENTED")}
        empty={strings.dashboard.tileAwaitingVerification.empty}
        to="/findings?state=IMPLEMENTED"
        action={strings.dashboard.tileAwaitingVerification.action}
        tone="attention"
        sparkSnapshot
      />
      <Tile
        testid="tile-negative"
        label={strings.dashboard.tileNegative.label}
        count={countOf(analytics.findingClasses, "NEGATIVE_OBSERVATION")}
        empty={strings.dashboard.tileNegative.empty}
        to="/findings?class=NEGATIVE_OBSERVATION"
        action={strings.dashboard.tileNegative.action}
        tone="danger"
        sparkSnapshot
      />
    </div>
  );
}

function TpiStatTiles({
  analytics,
  scheduledWitnessCount,
  openCount,
}: {
  analytics: PortfolioAnalytics;
  scheduledWitnessCount: number;
  openCount: number;
}): React.JSX.Element {
  const totalInspections = analytics.monthly.verificationsConducted.reduce(
    (sum, bucket) => sum + bucket.count,
    0,
  );
  return (
    <div className="q-tiles" data-testid="stat-tiles">
      <Tile
        testid="tile-scheduled-witness"
        label={strings.dashboard.tileWitnessNext14.label}
        count={scheduledWitnessCount}
        empty={strings.dashboard.tileWitnessNext14.empty}
        to="/docket?days=14"
        action={strings.dashboard.tileWitnessNext14.action}
        tone="attention"
        sparkSnapshot
      />
      <Tile
        testid="tile-inspections"
        label="Conducted inspections"
        count={totalInspections}
        empty="No inspections conducted yet."
        to="/inspections"
        action="Open inspections"
        tone="success"
        sparkSnapshot
      />
      {/* Wave W1 (ruling 6): the third party endorses nothing; its third tile is its
          own open work, landing on exactly that queue. */}
      <Tile
        testid="tile-tpi-open"
        label={strings.dashboard.tileTpiOpen.label}
        count={openCount}
        empty={strings.dashboard.tileTpiOpen.empty}
        to={QUEUES.myInspections.route}
        action={strings.dashboard.tileTpiOpen.action}
        tone="attention"
        sparkSnapshot
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Per-Persona Dashboard Canvas Compositions (ARR §5)                */
/* ------------------------------------------------------------------ */

function ExecutiveDashboard({
  analytics,
  dossiers,
  upcoming,
  recent,
  topFindings,
  observations,
  topItps,
}: {
  analytics: PortfolioAnalytics;
  dossiers: readonly ContractorDossier[];
  upcoming: readonly CalendarActivity[];
  recent: readonly CalendarActivity[];
  topFindings: readonly FindingSummary[];
  observations: readonly FindingSummary[];
  topItps: readonly ItpSummary[];
}): React.JSX.Element {
  return (
    <>
      <ExecutiveStatTiles analytics={analytics} />

      <ExecutiveAlert dossiers={dossiers} />

      {dossiers.length > 0 && (
        <Section
          heading={strings.dashboard.contractorStatsHeading}
          lede={strings.dashboard.contractorStatsLede}
        >
          <DossierTable rows={dossiers} />
        </Section>
      )}

      <Section heading={strings.dashboard.qualityHeading} lede={strings.dashboard.qualityCaption}>
        <QualityChart analytics={analytics} />
      </Section>

      <Section heading={strings.dashboard.trendHeading} lede={strings.dashboard.trendCaption}>
        <TrendChart analytics={analytics} />
        {topFindings.length > 0 && <TrendPair rows={topFindings} />}
      </Section>

      <Section
        heading={strings.dashboard.rootCauseHeading}
        lede={strings.dashboard.rootCauseCaption}
      >
        <RootCauseChart analytics={analytics} />
      </Section>

      <Section
        heading={strings.dashboard.divergingHeading}
        lede={strings.dashboard.divergingCaption}
      >
        <ObservationsChart findings={observations} />
      </Section>

      <Section heading={strings.dashboard.openItemsHeading}>
        <OpenItemsChart analytics={analytics} />
        <OpenItemsPair analytics={analytics} />
      </Section>

      <Section heading={strings.dashboard.myItpsHeading}>
        <ItpStates analytics={analytics} />
        {topItps.length > 0 && <ItpPair rows={topItps} />}
      </Section>

      {upcoming.length > 0 && (
        <Section heading={strings.dashboard.next14Heading} lede={strings.dashboard.next14Lede}>
          <FeedTable rows={upcoming} testid="next14-table" />
        </Section>
      )}

      {recent.length > 0 && (
        <Section heading={strings.dashboard.recentHeading} lede={strings.dashboard.recentLede}>
          <FeedTable rows={recent} testid="recent-table" />
        </Section>
      )}

      <Section heading={strings.dashboard.guideCard.label}>
        <p className="q-row">
          <Link className="q-link" to="/guide">
            {strings.dashboard.guideCard.action}
          </Link>
        </p>
      </Section>
    </>
  );
}

function QcLeadDashboard({
  analytics,
  dossiers,
  upcoming,
  topFindings,
  observations,
  topItps,
  mine,
}: {
  analytics: PortfolioAnalytics;
  dossiers: readonly ContractorDossier[];
  upcoming: readonly CalendarActivity[];
  topFindings: readonly FindingSummary[];
  observations: readonly FindingSummary[];
  topItps: readonly ItpSummary[];
  mine: readonly VerificationSummary[];
}): React.JSX.Element {
  const submittedItps = topItps.filter((itp) => itp.workflowState === "SUBMITTED");
  const finalVerifications = mine.filter((v) => v.workflowState === "FINAL_APPROVAL");
  const implementedFindings = topFindings.filter((f) => f.findingStatus === "IMPLEMENTED");

  return (
    <>
      <QcLeadStatTiles analytics={analytics} />

      <Section
        heading="Approvals Docket"
        lede="Records awaiting your technical sign-off and approval"
      >
        <ApprovalsDocket
          submittedItps={submittedItps}
          finalVerifications={finalVerifications}
          implementedFindings={implementedFindings}
        />
      </Section>

      {upcoming.length > 0 ? (
        <Section heading={strings.dashboard.next14Heading} lede={strings.dashboard.next14Lede}>
          <FeedTable rows={upcoming} testid="next14-table" />
        </Section>
      ) : (
        <Section heading={strings.dashboard.next14Heading}>
          <EmptyState title={strings.dashboard.next14Empty} body={strings.dashboard.next14Empty} />
        </Section>
      )}

      <Section heading={strings.dashboard.trendHeading} lede={strings.dashboard.trendCaption}>
        <TrendChart analytics={analytics} />
        {topFindings.length > 0 && <TrendPair rows={topFindings} />}
      </Section>

      <Section heading={strings.dashboard.openItemsHeading}>
        <OpenItemsChart analytics={analytics} />
        <OpenItemsPair analytics={analytics} />
      </Section>

      <Section heading={strings.dashboard.myItpsHeading}>
        <ItpStates analytics={analytics} />
        {topItps.length > 0 && <ItpPair rows={topItps} />}
      </Section>

      {dossiers.length > 0 && (
        <Section
          heading={strings.dashboard.contractorStatsHeading}
          lede={strings.dashboard.contractorStatsLede}
        >
          <DossierTable rows={dossiers} />
        </Section>
      )}

      <Section heading={strings.dashboard.qualityHeading} lede={strings.dashboard.qualityCaption}>
        <QualityChart analytics={analytics} />
      </Section>

      <Section
        heading={strings.dashboard.rootCauseHeading}
        lede={strings.dashboard.rootCauseCaption}
      >
        <RootCauseChart analytics={analytics} />
      </Section>

      <Section
        heading={strings.dashboard.divergingHeading}
        lede={strings.dashboard.divergingCaption}
      >
        <ObservationsChart findings={observations} />
      </Section>

      <Section heading={strings.dashboard.guideCard.label}>
        <p className="q-row">
          <Link className="q-link" to="/guide">
            {strings.dashboard.guideCard.action}
          </Link>
        </p>
      </Section>
    </>
  );
}

function ContractorDashboard({
  analytics,
  dossiers,
  upcoming,
  topFindings,
  observations,
  topItps,
}: {
  analytics: PortfolioAnalytics;
  dossiers: readonly ContractorDossier[];
  upcoming: readonly CalendarActivity[];
  topFindings: readonly FindingSummary[];
  observations: readonly FindingSummary[];
  topItps: readonly ItpSummary[];
}): React.JSX.Element {
  const openFindings = topFindings.filter((f) =>
    OPEN_FINDING_STATES.includes(f.findingStatus as (typeof OPEN_FINDING_STATES)[number]),
  );

  return (
    <>
      <ContractorStatTiles analytics={analytics} />

      <Section
        heading="Findings to answer & rectify"
        lede="Open findings assigned to your contract requiring response or rectification"
      >
        <FindingsToAnswerTable rows={openFindings} />
      </Section>

      {upcoming.length > 0 ? (
        <Section heading={strings.dashboard.next14Heading} lede={strings.dashboard.next14Lede}>
          <FeedTable rows={upcoming} testid="next14-table" />
        </Section>
      ) : (
        <Section heading={strings.dashboard.next14Heading}>
          <EmptyState title={strings.dashboard.next14Empty} body={strings.dashboard.next14Empty} />
        </Section>
      )}

      <Section heading={strings.dashboard.myItpsHeading}>
        <ItpStates analytics={analytics} />
        {topItps.length > 0 && <ItpPair rows={topItps} />}
      </Section>

      {dossiers.length > 0 && (
        <Section
          heading={strings.dashboard.contractorStatsHeading}
          lede={strings.dashboard.contractorStatsLede}
        >
          <DossierTable rows={dossiers} />
        </Section>
      )}

      <Section heading={strings.dashboard.qualityHeading} lede={strings.dashboard.qualityCaption}>
        <QualityChart analytics={analytics} />
      </Section>

      <Section
        heading={strings.dashboard.divergingHeading}
        lede={strings.dashboard.divergingCaption}
      >
        <ObservationsChart findings={observations} />
      </Section>

      <Section heading={strings.dashboard.guideCard.label}>
        <p className="q-row">
          <Link className="q-link" to="/guide">
            {strings.dashboard.guideCard.action}
          </Link>
        </p>
      </Section>
    </>
  );
}

function InspectorDashboard({
  analytics,
  upcoming,
  recent,
  mine,
  mineLoading,
  mineFeed,
  topFindings,
}: {
  analytics: PortfolioAnalytics;
  upcoming: readonly CalendarActivity[];
  recent: readonly CalendarActivity[];
  mine: readonly VerificationSummary[];
  mineLoading: boolean;
  mineFeed: readonly CalendarActivity[];
  topFindings: readonly FindingSummary[];
}): React.JSX.Element {
  const pendingCount = mine.filter((i) => !CLOSED_VERIFICATION_STATES.has(i.workflowState)).length;
  const implementedFindings = topFindings.filter((f) => f.findingStatus === "IMPLEMENTED");

  return (
    <>
      <Section heading={strings.dashboard.myWorkHeading} lede={strings.dashboard.myWorkLede}>
        {mineLoading && <LoadingLine label={strings.common.loading} />}
        {!mineLoading &&
          (mine.length === 0 ? (
            <EmptyState title={strings.dashboard.myWorkEmpty} />
          ) : (
            <MyWork rows={mine} />
          ))}
      </Section>

      <InspectorStatTiles
        analytics={analytics}
        assignedCount={mine.length}
        pendingCount={pendingCount}
      />

      <Section heading={strings.dashboard.myCalendarHeading}>
        {mineFeed.length === 0 ? (
          <EmptyState title={strings.dashboard.myCalendarEmpty} />
        ) : (
          <MyCalendar rows={mineFeed} />
        )}
      </Section>

      {upcoming.length > 0 && (
        <Section heading={strings.dashboard.next14Heading} lede={strings.dashboard.next14Lede}>
          <FeedTable rows={upcoming} testid="next14-table" />
        </Section>
      )}

      <Section
        heading="Findings awaiting field verification"
        lede="Implemented contractor rectifications ready for field witness and closure"
      >
        <FindingsAwaitingVerificationTable rows={implementedFindings} />
      </Section>

      <Section heading={strings.dashboard.qualityHeading} lede={strings.dashboard.qualityCaption}>
        <QualityChart analytics={analytics} />
      </Section>

      {recent.length > 0 && (
        <Section heading={strings.dashboard.recentHeading} lede={strings.dashboard.recentLede}>
          <FeedTable rows={recent} testid="recent-table" />
        </Section>
      )}

      <Section heading={strings.dashboard.guideCard.label}>
        <p className="q-row">
          <Link className="q-link" to="/guide">
            {strings.dashboard.guideCard.action}
          </Link>
        </p>
      </Section>
    </>
  );
}

function TpiDashboard({
  analytics,
  upcoming,
  mine,
  mineLoading,
  mineFeed,
  topItps,
}: {
  analytics: PortfolioAnalytics;
  upcoming: readonly CalendarActivity[];
  mine: readonly VerificationSummary[];
  mineLoading: boolean;
  mineFeed: readonly CalendarActivity[];
  topItps: readonly ItpSummary[];
}): React.JSX.Element {
  return (
    <>
      <Section
        heading="Third-Party Witness Docket"
        lede="Upcoming independent witness points and hold tests requiring your attendance"
      >
        {upcoming.length > 0 ? (
          <FeedTable rows={upcoming} testid="next14-table" />
        ) : (
          <EmptyState
            title="No upcoming witness points"
            body="No third-party witness points or tests are currently scheduled in the coming fortnight."
          />
        )}
      </Section>

      <TpiStatTiles
        analytics={analytics}
        scheduledWitnessCount={upcoming.length}
        openCount={mine.filter((row) => !DONE_VERIFICATION_STATES.has(row.workflowState)).length}
      />

      <Section
        heading={strings.dashboard.myWorkHeading}
        lede="Assigned third-party verification records"
      >
        {mineLoading && <LoadingLine label={strings.common.loading} />}
        {!mineLoading &&
          (mine.length === 0 ? (
            <EmptyState title={strings.dashboard.myWorkEmpty} />
          ) : (
            <MyWork rows={mine} />
          ))}
      </Section>

      <Section
        heading="Governing Inspection & Test Plans"
        lede="Plans defining third-party witness and hold point requirements"
      >
        {topItps.length > 0 ? (
          <ItpPair rows={topItps} />
        ) : (
          <EmptyState title={strings.dashboard.myItpsEmpty} body={strings.dashboard.myItpsEmpty} />
        )}
      </Section>

      <Section
        heading={strings.dashboard.myCalendarHeading}
        lede="Confirmed third-party callout and witness dates this month"
      >
        {mineFeed.length === 0 ? (
          <EmptyState title={strings.dashboard.myCalendarEmpty} />
        ) : (
          <MyCalendar rows={mineFeed} />
        )}
      </Section>

      <Section heading={strings.dashboard.guideCard.label}>
        <p className="q-row">
          <Link className="q-link" to="/guide">
            {strings.dashboard.guideCard.action}
          </Link>
        </p>
      </Section>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Main Dashboard Page Container                                      */
/* ------------------------------------------------------------------ */

export function DashboardPage({
  me,
  hideGreeting = false,
}: {
  me: Me;
  hideGreeting?: boolean;
}): React.JSX.Element {
  const token = useToken();
  const persona = resolveDashboardPersona(me);

  const analytics = useQuery((signal) => getPortfolioAnalytics(token, signal), [token]);
  const dossiers = useQuery((signal) => listContractorDossiers(token, signal), [token]);
  const upcoming = useQuery(
    (signal) =>
      listCalendarActivities(token, { fromDate: isoToday(), toDate: isoOffset(13), signal }),
    [token],
  );
  const recent = useQuery(
    (signal) =>
      listCalendarActivities(token, { fromDate: isoOffset(-30), toDate: isoOffset(-1), signal }),
    [token],
  );
  const mine = useQuery((signal) => listVerifications(token, { mine: true, signal }), [token]);
  const topFindings = useQuery(
    (signal) => listFindings(token, { limit: PAIR_LIMIT, signal }),
    [token],
  );
  const observations = useQuery((signal) => listFindings(token, { limit: 500, signal }), [token]);
  const topItps = useQuery((signal) => listItps(token, { signal }), [token]);
  const mineFeed = useQuery(
    (signal) =>
      listCalendarActivities(token, {
        fromDate: toISO(new Date().getFullYear(), new Date().getMonth(), 1),
        toDate: toISO(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getUTCDate(),
        ),
        mine: true,
        signal,
      }),
    [token],
  );

  const readsFailed = [analytics, dossiers].every((one) => one.kind === "failed");

  return (
    <div className="q-dash-grid" data-testid="dash-grid" data-persona={persona}>
      {!hideGreeting && (
        <>
          <h2 className="q-shell__heading" data-testid="greeting">
            {strings.shell.greeting(me.name)}
          </h2>
          <p className="q-shell__lede">{strings.dashboard.lede}</p>
        </>
      )}

      {readsFailed && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.dashboard.failed}</p>
          <p className="q-thread__body">{strings.dashboard.errorDetail}</p>
        </div>
      )}

      {analytics.kind === "loading" && (
        <>
          <SkeletonTiles count={4} />
          <SkeletonChart />
        </>
      )}

      {analytics.kind === "ready" && (
        <>
          {persona === "manager" && (
            <ExecutiveDashboard
              analytics={analytics.data}
              dossiers={dossiers.kind === "ready" ? dossiers.data : []}
              upcoming={upcoming.kind === "ready" ? upcoming.data.items : []}
              recent={recent.kind === "ready" ? recent.data.items : []}
              topFindings={topFindings.kind === "ready" ? topFindings.data.items : []}
              observations={observations.kind === "ready" ? observations.data.items : []}
              topItps={topItps.kind === "ready" ? topItps.data.items : []}
            />
          )}

          {persona === "qcLead" && (
            <QcLeadDashboard
              analytics={analytics.data}
              dossiers={dossiers.kind === "ready" ? dossiers.data : []}
              upcoming={upcoming.kind === "ready" ? upcoming.data.items : []}
              topFindings={topFindings.kind === "ready" ? topFindings.data.items : []}
              observations={observations.kind === "ready" ? observations.data.items : []}
              topItps={topItps.kind === "ready" ? topItps.data.items : []}
              mine={mine.kind === "ready" ? mine.data.items : []}
            />
          )}

          {persona === "contractor" && (
            <ContractorDashboard
              analytics={analytics.data}
              dossiers={dossiers.kind === "ready" ? dossiers.data : []}
              upcoming={upcoming.kind === "ready" ? upcoming.data.items : []}
              topFindings={topFindings.kind === "ready" ? topFindings.data.items : []}
              observations={observations.kind === "ready" ? observations.data.items : []}
              topItps={topItps.kind === "ready" ? topItps.data.items : []}
            />
          )}

          {persona === "inspector" && (
            <InspectorDashboard
              analytics={analytics.data}
              upcoming={upcoming.kind === "ready" ? upcoming.data.items : []}
              recent={recent.kind === "ready" ? recent.data.items : []}
              mine={mine.kind === "ready" ? mine.data.items : []}
              mineLoading={mine.kind === "loading"}
              mineFeed={mineFeed.kind === "ready" ? mineFeed.data.items : []}
              topFindings={topFindings.kind === "ready" ? topFindings.data.items : []}
            />
          )}

          {persona === "tpi" && (
            <TpiDashboard
              analytics={analytics.data}
              upcoming={upcoming.kind === "ready" ? upcoming.data.items : []}
              mine={mine.kind === "ready" ? mine.data.items : []}
              mineLoading={mine.kind === "loading"}
              mineFeed={mineFeed.kind === "ready" ? mineFeed.data.items : []}
              topItps={topItps.kind === "ready" ? topItps.data.items : []}
            />
          )}
        </>
      )}
    </div>
  );
}
